import {readFileAsArrayBuffer} from './upload.helpers';
import {retryWithBackoff} from './upload.retry';
import {MultipartDescriptor, MultipartOrder, MultipartPart, UploadRetryOptions} from './upload.types';

/** Parts uploaded at the same time; at most this many parts of one file are in memory (spec R7). */
export const MULTIPART_CONCURRENCY = 3;

/** Storage answers 403 once the presigned part URLs have expired (1 hour after the order). */
const EXPIRED_URL_STATUS = 403;

export interface PartRange {
  index: number;
  start: number;
  end: number;
}

export interface MultipartDeps {
  putPart: (url: string, body: ArrayBuffer) => Promise<Response>;
  complete: (descriptor: MultipartDescriptor) => Promise<unknown>;
  onPartDone?: (done: number, total: number) => void;
  retry?: UploadRetryOptions;
}

/**
 * Byte ranges for the parts, in the order of the descriptor's parts (spec R2).
 * Throws MULTIPART_PLAN_MISMATCH when the backend's plan doesn't fit the file.
 */
export function planPartRanges(fileSize: number, partSize: number, partCount: number): PartRange[] {
  if (partCount < 1 || Math.ceil(fileSize / partSize) !== partCount) {
    throw new Error('MULTIPART_PLAN_MISMATCH');
  }
  return Array.from({length: partCount}, (_, index) => ({
    index,
    start: index * partSize,
    end: Math.min((index + 1) * partSize, fileSize),
  }));
}

/**
 * Upload a file in parts straight to storage, then ask the backend to commit it (P10-900).
 * Each part is read from disk on its own and retried on its own. Nothing is committed unless every part
 * has an ETag (spec R5).
 */
export async function uploadMultipart(file: Blob, order: MultipartOrder, deps: MultipartDeps): Promise<void> {
  const parts = order.descriptor.parts;
  const ranges = planPartRanges(file.size, order.part_size, parts.length);
  let done = 0;
  const etags = await runWithConcurrency(ranges.map(range => async () => {
    const etag = await uploadPart(file, parts[range.index], range, deps);
    deps.onPartDone?.(++done, ranges.length);
    return etag;
  }), MULTIPART_CONCURRENCY);
  const descriptor = {
    ...order.descriptor,
    parts: parts.map((part, i) => ({...part, etagId: etags[i]})),
  };
  await deps.complete(descriptor);
}

async function uploadPart(file: Blob, part: MultipartPart, range: PartRange, deps: MultipartDeps): Promise<string> {
  let lastError: any;
  const response = await retryWithBackoff(
    async () => deps.putPart(part.url, await readFileAsArrayBuffer(file.slice(range.start, range.end))),
    error => lastError = error,
    undefined,
    {...deps.retry, isRetryable: error => error?.status !== EXPIRED_URL_STATUS},
  );
  if (!response) {
    throw new Error(lastError?.status === EXPIRED_URL_STATUS ? 'MULTIPART_URL_EXPIRED' : 'MULTIPART_PART_FAILED');
  }
  const etag = response.headers.get('ETag');
  if (!etag) {
    throw new Error('MULTIPART_MISSING_ETAG');
  }
  return etag;
}

/**
 * Run tasks with at most `limit` at a time. After the first failure no new task starts, and the first error
 * is thrown only once the tasks already running have finished, so no upload continues after a rejection.
 */
async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
  const results: T[] = [];
  let next = 0;
  let failure: {error: unknown} | null = null;
  const worker = async () => {
    while (!failure && next < tasks.length) {
      const index = next++;
      try {
        results[index] = await tasks[index]();
      } catch (error) {
        failure = failure ?? {error};
      }
    }
  };
  await Promise.all(Array.from({length: Math.min(limit, tasks.length)}, worker));
  if (failure) {
    throw failure.error;
  }
  return results;
}
